/**
 * Stats Card Component for Dashboard
 */
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatPercent } from '../../utils/format';

interface StatsCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    direction: 'up' | 'down';
  };
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | string;
}

const StatsCard = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  color = 'primary',
}: StatsCardProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isRTL = theme.direction === 'rtl';

  const getColorValue = () => {
    // If color is a hex string, return it directly
    if (color && color.startsWith('#')) {
      return color;
    }
    
    switch (color) {
      case 'primary':
        return theme.palette.primary.main;
      case 'secondary':
        return theme.palette.secondary.main;
      case 'success':
        return theme.palette.success.main;
      case 'warning':
        return theme.palette.warning.main;
      case 'error':
        return theme.palette.error.main;
      case 'info':
        return theme.palette.info.main;
      default:
        return color || theme.palette.primary.main;
    }
  };

  return (
    <Card>
      <CardContent>
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start', 
            flexDirection: isRTL ? 'row-reverse' : 'row',
            gap: 2,
            width: '100%',
          }}
        >
          <Box 
            sx={{ 
              textAlign: isRTL ? 'right' : 'left',
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, flexDirection: isRTL ? 'row-reverse' : 'row', flexWrap: 'wrap', gap: 0.5 }}>
                {trend.direction === 'up' ? (
                  <TrendingUp sx={{ fontSize: 16, color: 'success.main' }} />
                ) : (
                  <TrendingDown sx={{ fontSize: 16, color: 'error.main' }} />
                )}
                <Typography
                  variant="caption"
                  color={trend.direction === 'up' ? 'success.main' : 'error.main'}
                  fontWeight={600}
                >
                  {formatPercent(trend.value, 1)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('components.statsCard.vsLastMonth')}
                </Typography>
              </Box>
            )}
          </Box>
          {icon && (
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: `${getColorValue()}15`,
                color: getColorValue(),
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default StatsCard;

